import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';

import { cn } from '../../lib/cn';

const DropdownMenuContext = createContext<{ close: () => void } | null>(null);

// DropdownMenu mínimo — sin dependencias nuevas (no Radix Dropdown).
//
// Decisión slice 5: usar implementación custom controlada en vez de instalar
// `@radix-ui/react-dropdown-menu`. Para v1 nos alcanza con: abrir/cerrar al
// click del trigger, cerrar al hacer click afuera o al apretar Escape, y soportar
// items con onClick + icono opcional. Si en slices futuros necesitamos navegación
// con flechas o sub-menúes, migramos a Radix sin tocar callers.

interface DropdownMenuProps {
  trigger: ReactNode;
  children: ReactNode;
  align?: 'left' | 'right';
  className?: string;
  /** Etiqueta accesible del trigger si éste no contiene texto explícito. */
  triggerAriaLabel?: string;
}

export function DropdownMenu({
  trigger,
  children,
  align = 'right',
  className,
  triggerAriaLabel,
}: DropdownMenuProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent): void {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('keydown', handleKey);
    return () => {
      window.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  return (
    <div ref={containerRef} className={cn('relative inline-block', className)}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={triggerAriaLabel}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center"
      >
        {trigger}
      </button>
      {open && (
        <DropdownMenuContext.Provider value={{ close: () => setOpen(false) }}>
          <div
            role="menu"
            className={cn(
              'absolute z-50 mt-1 min-w-[12rem] rounded-md border border-marca-neutro bg-surface py-1 shadow-md focus:outline-none',
              align === 'right' ? 'right-0' : 'left-0',
            )}
          >
            {children}
          </div>
        </DropdownMenuContext.Provider>
      )}
    </div>
  );
}

interface DropdownMenuItemProps {
  onClick?: () => void;
  disabled?: boolean;
  icon?: ReactNode;
  children: ReactNode;
  variant?: 'default' | 'danger';
}

export function DropdownMenuItem({
  onClick,
  disabled,
  icon,
  children,
  variant = 'default',
}: DropdownMenuItemProps): JSX.Element {
  const ctx = useContext(DropdownMenuContext);
  const handleClick = (): void => {
    onClick?.();
    ctx?.close();
  };
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={handleClick}
      className={cn(
        'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors focus:outline-none',
        variant === 'danger' ? 'text-danger hover:bg-danger/5' : 'text-marca-gris hover:bg-marca-neutro',
        disabled && 'cursor-not-allowed opacity-50 hover:bg-transparent',
      )}
    >
      {icon && <span className="shrink-0">{icon}</span>}
      <span className="flex-1">{children}</span>
    </button>
  );
}

export function DropdownMenuSeparator(): JSX.Element {
  return <div role="separator" className="my-1 border-t border-marca-neutro" />;
}
